<template>
    <loading-element v-if="authStore.isLoading" />
    <div class="main bg-primary" v-else>
        <div class="rowSpaceBetween fullWidth mb-5">
            <TopDeck
                v-if="authStore.userInfos"
                firstElement="Catalogue"
                secondElement="d'ingrédients de base"
                :coloredSecond="true"
                actionHeaderIcon="mdi-arrow-left"
                :actionheaderFunction="() => $router.push('/my_ingredients')"
                actionHeaderColor="accent"
                actionHeaderTextColor="white"
            />
            <div class="rowCustom">
                <ThemeToggle />
                <AccountToggle />
            </div>
        </div>

        <div v-if="isAdmin" class="mb-3" style="display:flex;gap:8px;flex-wrap:wrap;">
            <v-btn
                color="accent"
                rounded="lg"
                @click="router.push('/base_ingredients')"
                class="white-text-button"
            >
                <v-icon class="mr-1">mdi-plus</v-icon>
                <span>Ajouter des ingrédients de base</span>
            </v-btn>

            <v-btn
                color="accent"
                rounded="lg"
                @click="router.push('/hidden_base_ingredients')"
                class="white-text-button"
            >
                <v-icon class="mr-1">mdi-plus</v-icon>
                <span>Ajouter des ingrédients cachés</span>
            </v-btn>
        </div>

        <v-row no-gutters>
            <!-- Colonne principale -->
            <v-col cols="12" :md="selectedIngredient ? 9 : 12" class="pr-md-2">
                <div class="pa-4 bg-secondary rounded-xl">

                    <!-- Cards catégories -->
                    <div class="categoryCards">
                        <div
                            v-for="cat in categoryCards"
                            :key="cat.key"
                            class="catCard"
                            :class="{ 'catCard--active': selectedCategory === cat.key }"
                            @click="selectedCategory = selectedCategory === cat.key ? 'Toutes' : cat.key"
                        >
                            <!-- Image de fond plein format -->
                            <v-img
                                v-if="cat.imageUrl"
                                :src="cat.imageUrl"
                                class="catCard__bg"
                                cover
                            />
                            <!-- Fallback emoji sans image -->
                            <div v-else class="catCard__fallback">
                                <span class="catCard__emoji">{{ cat.emoji }}</span>
                            </div>

                            <!-- Gradient + label par-dessus -->
                            <div class="catCard__overlay">
                                <span class="catCard__label">{{ cat.label }}</span>
                                <span class="catCard__count">{{ cat.count }}</span>
                            </div>

                            <!-- Crayon admin -->
                            <button
                                v-if="isAdmin"
                                class="catCard__editBtn"
                                @click.stop="openCategoryImageDialog(cat)"
                                title="Modifier l'image"
                            >
                                <v-icon size="11">mdi-pencil</v-icon>
                            </button>
                        </div>
                    </div>

                    <!-- Barre de recherche -->
                    <div class="searchRow mb-3">
                        <v-text-field
                            v-model="searchQuery"
                            placeholder="Rechercher un ingrédient..."
                            hide-details
                            density="compact"
                            variant="tonal"
                            prepend-inner-icon="mdi-magnify"
                            class="searchField rounded-lg bg-tertiary"
                            rounded="lg"
                        />
                    </div>

                    <!-- Filtres saisonnalité -->
                    <div class="season-filters mb-4">
                        <button
                            class="season-filter-btn"
                            :class="{ 'season-filter-btn--active': seasonFilter === 'winter' }"
                            @click="seasonFilter = seasonFilter === 'winter' ? null : 'winter'"
                        >❄️ Hiver</button>
                        <button
                            class="season-filter-btn"
                            :class="{ 'season-filter-btn--active': seasonFilter === 'summer' }"
                            @click="seasonFilter = seasonFilter === 'summer' ? null : 'summer'"
                        >☀️ Été</button>
                        <button
                            class="season-filter-btn season-filter-btn--frozen"
                            :class="{ 'season-filter-btn--active': seasonFilter === 'frozen' }"
                            @click="seasonFilter = seasonFilter === 'frozen' ? null : 'frozen'"
                        >🧊 Surgelé</button>
                    </div>

                    <!-- Loader -->
                    <div v-if="isLoadingIngredients" class="d-flex justify-center my-10">
                        <v-progress-circular indeterminate color="primary" />
                    </div>

                    <!-- Vide -->
                    <v-alert
                        v-else-if="!filteredIngredients.length"
                        type="info"
                        variant="tonal"
                        class="mt-4"
                    >
                        Aucun ingrédient à afficher.
                    </v-alert>

                    <!-- Tableau -->
                    <v-table density="compact" class="bg-tertiary rounded-lg" v-if="filteredIngredients.length">
                        <thead>
                        <tr>
                            <th style="width:48px;">Image</th>
                            <th class="th-sortable" @click="setSort('name')">
                                Nom
                                <span class="sort-arrow">{{ sortKey === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
                            </th>
                            <th class="th-sortable" @click="setSort('category')">
                                Catégorie
                                <span class="sort-arrow">{{ sortKey === 'category' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
                            </th>
                            <th class="th-sortable" @click="setSort('nutriscore')">
                                Nutriscore
                                <span class="sort-arrow">{{ sortKey === 'nutriscore' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
                            </th>
                            <th class="th-sortable" @click="setSort('kcal')">
                                Kcal/100g
                                <span class="sort-arrow">{{ sortKey === 'kcal' ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}</span>
                            </th>
                            <th style="width:110px;"></th>
                            <th v-if="isAdmin" style="width:48px;"></th>
                            <th v-if="isAdmin" style="width:48px;"></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr
                            v-for="item in filteredIngredients"
                            :key="item.id"
                            class="ingredients ingredient-row"
                            :class="{ 'row--selected': selectedIngredient?.id === item.id }"
                            @click="selectIngredient(item)"
                        >
                            <td class="ingredient-cell">
                                <div class="ingredient-content">
                                    <v-img
                                        v-if="item.productImage"
                                        :src="item.productImage"
                                        width="32"
                                        height="32"
                                        cover
                                        class="rounded"
                                    />
                                    <v-icon v-else color="grey-lighten-1" size="32">mdi-food</v-icon>
                                </div>
                            </td>
                            <td class="font-weight-medium">
                                {{ item.productName }}
                                <span v-if="getSeasonIcon(item.seasonMonths)" class="season-icon" :title="'Saison : ' + getSeasonIcon(item.seasonMonths)">{{ getSeasonIcon(item.seasonMonths) }}</span>
                                <span v-if="item.isFrozen" class="season-icon" title="Surgelé">🧊</span>
                            </td>
                            <td>
                                <span class="text-caption text-medium-emphasis">{{ item.category }}</span>
                            </td>
                            <td>
                                <v-chip
                                    v-if="item.nutriscore"
                                    :color="nutriscoreColor(item.nutriscore)"
                                    size="x-small"
                                    label
                                    class="font-weight-bold text-white px-2"
                                >
                                    {{ item.nutriscore }}
                                </v-chip>
                                <span v-else class="text-caption text-medium-emphasis">—</span>
                            </td>
                            <td>
                                <span class="text-caption font-weight-medium">{{ getKcal(item) }}</span>
                            </td>
                            <td @click.stop>
                                <button
                                    class="ing-add-btn"
                                    :class="{ 'ing-add-btn--added': addedIds.has(item.id) }"
                                    :disabled="addedIds.has(item.id)"
                                    @click.stop="addItemToUserCollection(item)"
                                >
                                    <v-icon size="13">{{ addedIds.has(item.id) ? 'mdi-check' : 'mdi-plus' }}</v-icon>
                                    {{ addedIds.has(item.id) ? 'Ajouté' : 'Ajouter' }}
                                </button>
                            </td>
                            <td v-if="isAdmin" @click.stop>
                                <v-btn
                                    @click.stop="openEditDialog(item)"
                                    size="small"
                                    color="accent"
                                    variant="tonal"
                                    icon
                                    rounded="lg"
                                >
                                    <v-icon size="16">mdi-pencil</v-icon>
                                </v-btn>
                            </td>
                            <td v-if="isAdmin" @click.stop>
                                <v-btn
                                    @click.stop="requestDelete(item)"
                                    size="small"
                                    color="error"
                                    variant="tonal"
                                    icon
                                    rounded="lg"
                                >
                                    <v-icon size="16">mdi-delete</v-icon>
                                </v-btn>
                            </td>
                        </tr>
                        </tbody>
                    </v-table>
                </div>
            </v-col>

            <!-- Panel latéral détail ingrédient -->
            <v-col v-if="selectedIngredient" cols="12" md="3" class="bg-secondary pa-4 rounded-xl mt-4 mt-md-0">
                <div class="d-flex align-start justify-space-between mb-4">
                    <h1 class="font-weight-bold text-h6 mb-0" style="flex:1;">
                        {{ selectedIngredient.productName }}
                    </h1>
                    <v-btn icon size="x-small" variant="text" @click="selectedIngredient = null" class="ml-2">
                        <v-icon size="18">mdi-close</v-icon>
                    </v-btn>
                </div>

                <!-- Image ingrédient -->
                <div class="d-flex justify-center mb-4">
                    <v-img
                        v-if="selectedIngredient.productImage"
                        :src="selectedIngredient.productImage"
                        width="120"
                        height="120"
                        cover
                        class="rounded-lg"
                        style="border:1.5px solid rgba(117,83,252,0.2);"
                    />
                    <div v-else class="ing-img-placeholder rounded-lg d-flex align-center justify-center">
                        <v-icon size="48" color="grey-lighten-1">mdi-food-apple-outline</v-icon>
                    </div>
                </div>

                <!-- Catégorie -->
                <v-chip size="small" color="accent" variant="tonal" class="mb-3" v-if="selectedIngredient.category">
                    {{ selectedIngredient.category }}
                </v-chip>

                <!-- Badge visible/caché -->
                <v-chip
                    size="small"
                    :color="selectedIngredient.isHidden ? 'grey' : 'green'"
                    variant="tonal"
                    class="mb-3 ml-1"
                >
                    <v-icon start size="12">{{ selectedIngredient.isHidden ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
                    {{ selectedIngredient.isHidden ? 'Caché (recettes)' : 'Visible (catalogue)' }}
                </v-chip>

                <!-- Saisonnalité & surgelé -->
                <div class="d-flex align-center gap-2 mb-3" v-if="getSeasonIcon(selectedIngredient.seasonMonths) || selectedIngredient.isFrozen">
                    <span v-if="getSeasonIcon(selectedIngredient.seasonMonths)" style="font-size:18px;" :title="'Mois de saison sélectionnés'">{{ getSeasonIcon(selectedIngredient.seasonMonths) }}</span>
                    <span v-if="selectedIngredient.isFrozen" style="font-size:18px;" title="Surgelé">🧊</span>
                </div>

                <!-- Marque -->
                <p class="text-caption text-medium-emphasis mb-3" v-if="selectedIngredient.productBrand">
                    {{ selectedIngredient.productBrand }}
                </p>

                <!-- Nutriments -->
                <div v-if="selectedIngredient.nutriments_map && selectedIngredient.nutriments_map.length">
                    <h4 class="mb-2" style="font-size:0.8rem;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;opacity:0.5;">Nutriments</h4>
                    <p
                        v-for="(item, index) in selectedIngredient.nutriments_map"
                        :key="index"
                        class="nutriment-line"
                    >
                        <span>{{ $t(item.key) }}</span>
                        <span>{{ item.value }}</span>
                    </p>
                </div>

                <!-- NOVA -->
                <div class="mt-3" v-if="selectedIngredient.novaGroup?.string">
                    <v-chip :color="selectedIngredient.novaGroup.color" size="small">
                        {{ $t(selectedIngredient.novaGroup.string) }}
                    </v-chip>
                </div>

                <!-- Bouton ajouter -->
                <v-btn
                    block
                    color="accent"
                    variant="tonal"
                    rounded="lg"
                    elevation="0"
                    class="mt-4"
                    @click="addItemToUserCollection(selectedIngredient)"
                >
                    <v-icon start size="16">mdi-plus</v-icon>
                    Ajouter à ma collection
                </v-btn>

                <!-- Bouton modifier (admin) -->
                <v-btn
                    v-if="isAdmin"
                    block
                    color="accent"
                    variant="outlined"
                    rounded="lg"
                    elevation="0"
                    class="mt-2"
                    @click="openEditDialog(selectedIngredient)"
                >
                    <v-icon start size="16">mdi-pencil</v-icon>
                    Modifier l'ingrédient
                </v-btn>

                <!-- Bouton promouvoir (admin, uniquement si caché) -->
                <v-btn
                    v-if="isAdmin && selectedIngredient.isHidden"
                    block
                    color="green"
                    variant="tonal"
                    rounded="lg"
                    elevation="0"
                    class="mt-2"
                    :loading="isPromoting"
                    @click="promoteToBase(selectedIngredient)"
                >
                    <v-icon start size="16">mdi-eye-outline</v-icon>
                    Rendre visible dans le catalogue
                </v-btn>
            </v-col>
        </v-row>

        <!-- ========== DIALOG ÉDITION INGRÉDIENT (admin) ========== -->
        <v-dialog v-model="editDialog.open" max-width="680" scrollable>
            <v-card>
                <v-card-title class="text-h6 pa-4 pb-2">Modifier l'ingrédient</v-card-title>
                <v-card-text class="pt-2">

                    <!-- Infos générales -->
                    <div class="dialog-section-label">Informations générales</div>
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-text-field v-model="editDialog.name" label="Nom *" variant="outlined" hide-details clearable />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model="editDialog.brand" label="Marque" variant="outlined" hide-details clearable />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-combobox v-model="editDialog.category" :items="ingredientCategories" label="Catégorie" variant="outlined" hide-details clearable />
                        </v-col>
                        <v-col cols="6" md="3">
                            <v-text-field v-model.number="editDialog.poids" label="Poids (g)" type="number" min="0" variant="outlined" hide-details />
                        </v-col>
                    </v-row>

                    <!-- Image -->
                    <div class="dialog-section-label mt-4">Image & lien produit</div>
                    <v-row align="center">
                        <v-col cols="12" md="8">
                            <v-text-field
                                v-model="editDialog.productImage"
                                label="URL de l'image"
                                variant="outlined"
                                hide-details
                                clearable
                                prepend-inner-icon="mdi-image-outline"
                            />
                        </v-col>
                        <v-col cols="12" md="4" class="d-flex justify-center">
                            <v-avatar v-if="editDialog.productImage" size="64" rounded="lg" style="border:1.5px solid rgba(117,83,252,0.3);">
                                <v-img :src="editDialog.productImage" cover />
                            </v-avatar>
                            <div v-else class="ing-img-placeholder rounded-lg d-flex align-center justify-center">
                                <v-icon size="28" color="grey-lighten-1">mdi-image-off-outline</v-icon>
                            </div>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                v-model="editDialog.productLink"
                                label="Lien vers la page produit (URL)"
                                variant="outlined"
                                hide-details
                                clearable
                                prepend-inner-icon="mdi-link"
                            />
                        </v-col>
                    </v-row>

                    <!-- Scores nutritionnels -->
                    <div class="dialog-section-label mt-4">Scores nutritionnels</div>
                    <v-row>
                        <v-col cols="12" md="4">
                            <v-select
                                v-model="editDialog.nutriscore"
                                :items="NUTRISCORE_OPTIONS"
                                label="Nutriscore"
                                variant="outlined"
                                hide-details
                                clearable
                            >
                                <template #selection="{ item }">
                                    <v-chip :color="nutriscoreColor(item.value)" size="small" label class="font-weight-bold text-white">
                                        {{ item.value || '—' }}
                                    </v-chip>
                                </template>
                                <template #item="{ item, props: p }">
                                    <v-list-item v-bind="p">
                                        <template #prepend>
                                            <v-chip :color="nutriscoreColor(item.value)" size="small" label class="font-weight-bold text-white mr-2">
                                                {{ item.value }}
                                            </v-chip>
                                        </template>
                                    </v-list-item>
                                </template>
                            </v-select>
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-select
                                v-model="editDialog.novaValue"
                                :items="NOVA_OPTIONS"
                                label="Score NOVA"
                                variant="outlined"
                                hide-details
                                clearable
                            >
                                <template #selection="{ item }">
                                    <v-chip :color="novaColor(item.value)" size="small" label class="font-weight-bold text-white">
                                        Nova {{ item.value }}
                                    </v-chip>
                                </template>
                                <template #item="{ item, props: p }">
                                    <v-list-item v-bind="p">
                                        <template #prepend>
                                            <v-chip :color="novaColor(item.value)" size="small" label class="font-weight-bold text-white mr-2">
                                                Nova {{ item.value }}
                                            </v-chip>
                                        </template>
                                    </v-list-item>
                                </template>
                            </v-select>
                        </v-col>
                    </v-row>

                    <!-- Valeurs nutritionnelles -->
                    <div class="dialog-section-label mt-4">Valeurs nutritionnelles (pour 100 g)</div>
                    <v-row>
                        <v-col cols="6" md="3">
                            <v-text-field v-model.number="editDialog.kcal" label="Kcal *" type="number" min="0" variant="outlined" hide-details />
                        </v-col>
                        <v-col cols="6" md="3">
                            <v-text-field v-model.number="editDialog.lipides" label="Lipides (g) *" type="number" min="0" variant="outlined" hide-details />
                        </v-col>
                        <v-col cols="6" md="3">
                            <v-text-field v-model.number="editDialog.agSatures" label="AG saturés (g)" type="number" min="0" variant="outlined" hide-details />
                        </v-col>
                        <v-col cols="6" md="3">
                            <v-text-field v-model.number="editDialog.glucides" label="Glucides (g) *" type="number" min="0" variant="outlined" hide-details />
                        </v-col>
                        <v-col cols="6" md="3">
                            <v-text-field v-model.number="editDialog.sucre" label="Sucres (g)" type="number" min="0" variant="outlined" hide-details />
                        </v-col>
                        <v-col cols="6" md="3">
                            <v-text-field v-model.number="editDialog.proteines" label="Protéines (g) *" type="number" min="0" variant="outlined" hide-details />
                        </v-col>
                        <v-col cols="6" md="3">
                            <v-text-field v-model.number="editDialog.fibres" label="Fibres (g)" type="number" min="0" variant="outlined" hide-details />
                        </v-col>
                        <v-col cols="6" md="3">
                            <v-text-field v-model.number="editDialog.sel" label="Sel (g)" type="number" min="0" variant="outlined" hide-details />
                        </v-col>
                    </v-row>

                    <!-- Magasins -->
                    <div class="dialog-section-label mt-4">Disponible dans ces magasins</div>
                    <div class="store-grid mt-2 mb-1">
                        <v-chip
                            v-for="store in AVAILABLE_STORES"
                            :key="store"
                            :color="editDialog.availableStores.includes(store) ? 'accent' : undefined"
                            :variant="editDialog.availableStores.includes(store) ? 'flat' : 'tonal'"
                            :class="editDialog.availableStores.includes(store) ? 'text-white font-weight-bold' : ''"
                            size="small"
                            label
                            class="store-chip"
                            @click="toggleEditStore(store)"
                        >
                            <v-icon start size="14">{{ editDialog.availableStores.includes(store) ? 'mdi-check' : 'mdi-store-outline' }}</v-icon>
                            {{ store }}
                        </v-chip>
                    </div>

                    <!-- Saisonnalité -->
                    <div class="dialog-section-label mt-4">Saisonnalité & conservation</div>
                    <SeasonalityPicker
                        v-model="editDialog.seasonMonths"
                        :frozen="editDialog.isFrozen"
                        @update:frozen="editDialog.isFrozen = $event"
                    />
                </v-card-text>

                <v-card-actions>
                    <v-spacer />
                    <v-btn variant="text" @click="editDialog.open = false">Annuler</v-btn>
                    <v-btn color="accent" :loading="editDialog.saving" :disabled="!canSaveEdit" @click="saveEditIngredient">
                        Enregistrer
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ========== DIALOG IMAGE CATÉGORIE (admin) ========== -->
        <v-dialog v-model="categoryImageDialog.open" max-width="480">
            <v-card>
                <v-card-title class="text-h6 pa-4 pb-2">
                    Image de la catégorie « {{ categoryImageDialog.label }} »
                </v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="categoryImageDialog.imageUrl"
                        label="URL de l'image"
                        variant="outlined"
                        hide-details
                        clearable
                        prepend-inner-icon="mdi-image-outline"
                        class="mb-4"
                    />
                    <div class="d-flex justify-center">
                        <v-avatar v-if="categoryImageDialog.imageUrl" size="96" rounded="lg" style="border:1.5px solid rgba(117,83,252,0.3);">
                            <v-img :src="categoryImageDialog.imageUrl" cover />
                        </v-avatar>
                        <div v-else class="ing-img-placeholder rounded-lg d-flex align-center justify-center" style="width:96px;height:96px;">
                            <v-icon size="36" color="grey-lighten-1">mdi-image-off-outline</v-icon>
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn variant="text" @click="categoryImageDialog.open = false">Annuler</v-btn>
                    <v-btn color="accent" :loading="categoryImageDialog.saving" @click="saveCategoryImage">Enregistrer</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Confirms & snackbars -->
        <v-snackbar v-model="snack.show" :timeout="2500">{{ snack.text }}</v-snackbar>
        <v-snackbar v-if="errorMessage" color="error" timeout="3000" top right>{{ errorMessage }}</v-snackbar>

        <ConfirmDialog
            v-model="confirmDialogOpen"
            title="Confirmer la suppression"
            :message="confirmDialogMessage"
            confirm-text="Supprimer"
            cancel-text="Annuler"
            confirm-color="error"
            cancel-color="grey"
            confirm-variant="elevated"
            cancel-variant="text"
            :max-width="480"
            @confirm="confirmDeleteBaseIngredient"
            @cancel="cancelDeleteBaseIngredient"
        />
    </div>
</template>

<script>
import { ref, onMounted, watch, computed } from "vue";
import { useRouter } from "vue-router";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "@/firebase";
import LoadingElement from "@/components/Animations/LoadingElement.vue";
import TopDeck from "@/components/Utils/TopDeck.vue";
import ConfirmDialog from "@/components/Utils/ConfirmDialog.vue";
import { useAuthStore } from "@/store/auth_store";
import ThemeToggle from "@/components/Utils/ThemeToggle.vue";
import { addItemToUserCollection as addItemToUserCollectionService } from '@/service/ingredient_service';
import {
    getBaseIngredients,
    deleteBaseIngredient,
    updateBaseIngredient,
    updateCategoryImage,
    getCategoryMeta,
    AVAILABLE_STORES,
} from '@/service/base_ingredient_service';
import {
    getHiddenBaseIngredients,
    deleteHiddenBaseIngredient,
} from '@/service/hidden_base_ingredient_service';
import AccountToggle from "@/components/Utils/AccountToggle.vue";
import SeasonalityPicker from "@/components/Kitchen/Ingredients/SeasonalityPicker.vue";

export default {
    name: "BaseIngredientsCatalog",
    components: { LoadingElement, TopDeck, ThemeToggle, AccountToggle, ConfirmDialog, SeasonalityPicker },
    setup() {
        const authStore = useAuthStore();
        const errorMessage = ref("");
        const snack = ref({ show: false, text: "" });
        const router = useRouter();

        const WINTER = new Set([11, 0, 1, 2]);
        const SUMMER = new Set([5, 6, 7, 8]);
        function getSeasonIcon(months) {
            if (!Array.isArray(months) || !months.length) return '';
            const hasWinter = months.some(m => WINTER.has(m));
            const hasSummer = months.some(m => SUMMER.has(m));
            if (hasWinter && hasSummer) return '❄️☀️';
            if (hasWinter) return '❄️';
            if (hasSummer) return '☀️';
            return '🌿';
        }

        const ingredients = ref([]);
        const isLoadingIngredients = ref(false);
        const searchQuery = ref("");
        const selectedCategory = ref("Toutes");
        const selectedIngredient = ref(null);
        const seasonFilter = ref(null); // null | 'winter' | 'summer' | 'frozen'
        const isAdmin = computed(() => authStore.userInfos?.role === 'admin');

        // ---- Suppression ----
        const confirmDialogOpen = ref(false);
        const ingredientToDelete = ref(null);
        const confirmDialogMessage = computed(() => {
            const name = ingredientToDelete.value?.productName || ingredientToDelete.value?.id || "";
            return name
                ? `Supprimer l'ingrédient de base "${name}" ?`
                : "Supprimer cet ingrédient de base ?";
        });

        // ---- Catégories ----
        const categoryMeta = ref({});

        const CATEGORY_META = {
            'légumes':      { emoji: '🥦', label: 'Légumes' },
            'fruits':       { emoji: '🍎', label: 'Fruits' },
            'poissons':     { emoji: '🐟', label: 'Poissons' },
            'légumineuses': { emoji: '🫘', label: 'Légumineuses' },
            'huiles':       { emoji: '🫙', label: 'Huiles' },
            'farines':      { emoji: '🌾', label: 'Farines' },
            'épices':       { emoji: '🌶️', label: 'Épices' },
        };

        const categories = computed(() => {
            const cats = new Set(
                ingredients.value.map((i) => i.category).filter(Boolean)
            );
            return ["Toutes", ...Array.from(cats).sort()];
        });

        const categoryCards = computed(() => {
            return categories.value
                .filter(c => c !== "Toutes")
                .map(cat => {
                    const key = cat.toLowerCase().trim();
                    const meta = Object.entries(CATEGORY_META).find(([k]) => key.includes(k));
                    const count = ingredients.value.filter(i => i.category === cat).length;
                    const metaImg = categoryMeta.value?.[cat]?.imageUrl || null;
                    return {
                        key: cat,
                        label: meta ? meta[1].label : cat,
                        emoji: meta ? meta[1].emoji : '🍽️',
                        imageUrl: metaImg,
                        count,
                    };
                });
        });

        // ---- Dialog image catégorie ----
        const categoryImageDialog = ref({ open: false, key: '', label: '', imageUrl: '', saving: false });

        function openCategoryImageDialog(cat) {
            categoryImageDialog.value = {
                open: true,
                key: cat.key,
                label: cat.label,
                imageUrl: cat.imageUrl || '',
                saving: false,
            };
        }

        async function saveCategoryImage() {
            categoryImageDialog.value.saving = true;
            try {
                await updateCategoryImage(
                    categoryImageDialog.value.key,
                    categoryImageDialog.value.imageUrl,
                    authStore.userInfos?.role
                );
                // Mise à jour locale
                categoryMeta.value = {
                    ...categoryMeta.value,
                    [categoryImageDialog.value.key]: { imageUrl: categoryImageDialog.value.imageUrl },
                };
                snack.value = { show: true, text: "Image de catégorie mise à jour ✅" };
                categoryImageDialog.value.open = false;
            } catch (e) {
                console.error("Erreur saveCategoryImage:", e);
                errorMessage.value = "Erreur lors de la mise à jour de l'image.";
            } finally {
                categoryImageDialog.value.saving = false;
            }
        }

        // ---- Constantes scores ----
        const NUTRISCORE_OPTIONS = ['A', 'B', 'C', 'D', 'E'];
        const NOVA_OPTIONS = [1, 2, 3, 4];

        const ingredientCategories = [
            "Fruits", "Légumes", "Légumineuses", "Céréales", "Viandes",
            "Poissons", "Produits laitiers", "Œufs", "Matières grasses",
            "Épices", "Herbes", "Autres"
        ];

        function nutriscoreColor(v) {
            const map = { A: 'green', B: 'light-green', C: 'yellow-darken-3', D: 'orange', E: 'red' };
            return map[v] || 'grey';
        }
        function novaColor(v) {
            const map = { 1: 'green', 2: 'light-green', 3: 'orange', 4: 'red' };
            return map[v] || 'grey';
        }
        function buildNovaGroup(novaValue) {
            if (!novaValue) return { color: 'grey', string: '' };
            return { color: novaColor(novaValue), string: `nova.${novaValue}` };
        }

        // Utilitaire: parse une valeur numérique d'un string type "264 kcal"
        function parseNumFromStr(s) {
            const m = String(s ?? '').match(/-?\d+(\.\d+)?/);
            return m ? Number(m[0]) : null;
        }

        // ---- Dialog édition ingrédient ----
        const emptyEditForm = () => ({
            open: false, saving: false, id: null,
            name: "", brand: "", category: "",
            poids: null, kcal: null, lipides: null, glucides: null,
            proteines: null, fibres: null, sel: null, sucre: null, agSatures: null,
            productImage: "", productLink: "",
            nutriscore: "", novaValue: null,
            availableStores: [],
            seasonMonths: [],
            isFrozen: false,
        });

        const editDialog = ref(emptyEditForm());

        const canSaveEdit = computed(() =>
            editDialog.value.name?.trim() &&
            editDialog.value.kcal !== null &&
            editDialog.value.lipides !== null &&
            editDialog.value.glucides !== null &&
            editDialog.value.proteines !== null
        );

        function getNutrimentValue(nutriments_map, key) {
            const entry = (nutriments_map || []).find(n => n.key === key);
            return entry ? parseNumFromStr(entry.value) : null;
        }

        function openEditDialog(item) {
            const nm = item.nutriments_map || [];
            editDialog.value = {
                open: true,
                saving: false,
                id: item.id,
                name: item.productName || "",
                brand: item.productBrand || "",
                category: item.category || "",
                poids: item.servingQuantity || null,
                productImage: item.productImage || "",
                productLink: item.productLink || "",
                nutriscore: item.nutriscore || "",
                novaValue: item.novaGroup?.string ? parseInt(item.novaGroup.string.replace('nova.', '')) : null,
                availableStores: Array.isArray(item.availableStores) ? [...item.availableStores] : [],
                seasonMonths: Array.isArray(item.seasonMonths) ? [...item.seasonMonths] : [],
                isFrozen: item.isFrozen || false,
                kcal: getNutrimentValue(nm, 'energy-kcal'),
                lipides: getNutrimentValue(nm, 'fat'),
                agSatures: getNutrimentValue(nm, 'saturated-fat'),
                glucides: getNutrimentValue(nm, 'carbohydrates'),
                sucre: getNutrimentValue(nm, 'sugar'),
                proteines: getNutrimentValue(nm, 'proteins'),
                fibres: getNutrimentValue(nm, 'fiber'),
                sel: getNutrimentValue(nm, 'salt'),
            };
        }

        function toggleEditStore(store) {
            const arr = editDialog.value.availableStores;
            const idx = arr.indexOf(store);
            if (idx === -1) arr.push(store);
            else arr.splice(idx, 1);
        }

        async function saveEditIngredient() {
            if (!canSaveEdit.value) return;
            editDialog.value.saving = true;
            try {
                const f = editDialog.value;
                const nutriments_map = [
                    { key: "energy-kcal", value: f.kcal != null ? `${f.kcal} kcal` : "" },
                    { key: "fat", value: f.lipides != null ? `${f.lipides} g` : "" },
                    { key: "saturated-fat", value: f.agSatures != null ? `${f.agSatures} g` : "" },
                    { key: "carbohydrates", value: f.glucides != null ? `${f.glucides} g` : "" },
                    { key: "sugar", value: f.sucre != null ? `${f.sucre} g` : "" },
                    { key: "proteins", value: f.proteines != null ? `${f.proteines} g` : "" },
                    { key: "fiber", value: f.fibres != null ? `${f.fibres} g` : "" },
                    { key: "salt", value: f.sel != null ? `${f.sel} g` : "" },
                ].filter(n => n.value);

                const updatedData = {
                    productName: f.name.trim(),
                    productBrand: f.brand || "",
                    category: f.category || "",
                    servingQuantity: f.poids || null,
                    servingSize: f.poids ? `${f.poids} g` : null,
                    productImage: f.productImage || "",
                    productLink: f.productLink || "",
                    nutriscore: f.nutriscore || "",
                    novaGroup: buildNovaGroup(f.novaValue),
                    availableStores: [...f.availableStores],
                    seasonMonths: [...f.seasonMonths],
                    isFrozen: f.isFrozen || false,
                    nutriments_map,
                };

                // Sauvegarde selon la collection d'origine (base ou cachée)
                const currentItem = ingredients.value.find(i => i.id === f.id);
                let merged;
                if (currentItem?.isHidden) {
                    // Ingrédient caché → écrire dans HiddenBaseIngredients/all
                    const hiddenRef = doc(db, "HiddenBaseIngredients", "all");
                    const snap = await getDoc(hiddenRef);
                    const hiddenData = snap.exists() ? snap.data() : {};
                    merged = { ...(hiddenData[f.id] || {}), ...updatedData, hidden: true };
                    await setDoc(hiddenRef, { [f.id]: merged }, { merge: true });
                } else {
                    merged = await updateBaseIngredient(f.id, updatedData, authStore.userInfos?.role);
                }

                // Mise à jour locale
                const idx = ingredients.value.findIndex(i => i.id === f.id);
                if (idx !== -1) {
                    ingredients.value[idx] = { ...ingredients.value[idx], ...merged };
                }
                if (selectedIngredient.value?.id === f.id) {
                    selectedIngredient.value = { ...selectedIngredient.value, ...merged };
                }

                snack.value = { show: true, text: "Ingrédient mis à jour ✅" };
                editDialog.value.open = false;
            } catch (err) {
                console.error("Erreur saveEditIngredient:", err);
                errorMessage.value = "Erreur lors de la mise à jour de l'ingrédient.";
            } finally {
                editDialog.value.saving = false;
            }
        }

        // ---- Sélection panel latéral ----
        function selectIngredient(item) {
            if (selectedIngredient.value?.id === item.id) {
                selectedIngredient.value = null;
            } else {
                selectedIngredient.value = item;
            }
        }

        // ---- Ajout à la collection utilisateur ----
        const addedIds = ref(new Set());

        function getKcal(item) {
            const entry = (item.nutriments_map || []).find(n => n.key === 'energy-kcal');
            if (!entry) return '—';
            const m = String(entry.value || '').match(/\d+(\.\d+)?/);
            return m ? `${Math.round(Number(m[0]))} kcal` : '—';
        }

        async function addItemToUserCollection(item) {
            if (!authStore?.userid) {
                errorMessage.value = "Utilisateur non connecté.";
                return;
            }
            try {
                const res = await addItemToUserCollectionService(authStore.userid, item, item.id);
                if (res?.existed) {
                    snack.value = { show: true, text: "Déjà dans votre collection." };
                } else {
                    snack.value = { show: true, text: "Ajouté à votre collection." };
                }
                // Dans les deux cas, marquer comme ajouté
                const next = new Set(addedIds.value);
                next.add(item.id);
                addedIds.value = next;
            } catch (e) {
                console.error('Erreur ajout ingrédient utilisateur:', e);
                errorMessage.value = "Erreur lors de l'ajout de l'ingrédient.";
            }
        }

        // ---- Suppression ----
        async function deleteBaseIngredientItem(item) {
            if (!item?.id) return;
            if (!isAdmin.value) {
                errorMessage.value = "Action réservée aux administrateurs.";
                return;
            }
            try {
                await deleteBaseIngredient(item.id, authStore.userInfos?.role);
                ingredients.value = ingredients.value.filter((ing) => ing.id !== item.id);
                if (selectedIngredient.value?.id === item.id) selectedIngredient.value = null;
                snack.value = { show: true, text: "Ingrédient supprimé." };
            } catch (e) {
                console.error("Erreur suppression ingrédient de base:", e);
                errorMessage.value = "Erreur lors de la suppression de l'ingrédient.";
            }
        }

        function requestDelete(item) {
            if (!item || !isAdmin.value) return;
            ingredientToDelete.value = item;
            confirmDialogOpen.value = true;
        }

        function cancelDeleteBaseIngredient() {
            confirmDialogOpen.value = false;
            ingredientToDelete.value = null;
        }

        async function confirmDeleteBaseIngredient() {
            if (!ingredientToDelete.value) return;
            const item = ingredientToDelete.value;
            ingredientToDelete.value = null;
            confirmDialogOpen.value = false;
            await deleteBaseIngredientItem(item);
        }

        // ---- Promotion hidden → base ----
        const isPromoting = ref(false);
        async function promoteToBase(item) {
            if (!item?.id || !isAdmin.value) return;
            isPromoting.value = true;
            try {
                const promoted = { ...item };
                delete promoted.id;
                delete promoted.isHidden;
                delete promoted.hidden;

                // Insérer dans BaseIngredients/all
                const baseRef = doc(db, "BaseIngredients", "all");
                await setDoc(baseRef, { [item.id]: promoted }, { merge: true });

                // Supprimer de HiddenBaseIngredients/all
                await deleteHiddenBaseIngredient(item.id, authStore.userInfos?.role);

                // Mise à jour locale
                const idx = ingredients.value.findIndex(i => i.id === item.id);
                if (idx !== -1) ingredients.value[idx] = { ...ingredients.value[idx], isHidden: false };
                if (selectedIngredient.value?.id === item.id) {
                    selectedIngredient.value = { ...selectedIngredient.value, isHidden: false };
                }

                snack.value = { show: true, text: "Ingrédient déplacé vers le catalogue ✅" };
            } catch (e) {
                console.error("Erreur promoteToBase:", e);
                errorMessage.value = "Erreur lors du déplacement de l'ingrédient.";
            } finally {
                isPromoting.value = false;
            }
        }

        // ---- Tri ----
        const sortKey = ref(null);
        const sortDir = ref('asc');
        const NUTRISCORE_ORDER = { A: 1, B: 2, C: 3, D: 4, E: 5 };

        function setSort(key) {
            if (sortKey.value === key) {
                sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
            } else {
                sortKey.value = key;
                sortDir.value = 'asc';
            }
        }

        function parseKcalNum(item) {
            const entry = (item.nutriments_map || []).find(n => n.key === 'energy-kcal');
            if (!entry) return -1;
            const m = String(entry.value || '').match(/\d+(\.\d+)?/);
            return m ? Number(m[0]) : -1;
        }

        // ---- Filtrage + tri ----
        const filteredIngredients = computed(() => {
            if (!ingredients.value) return [];
            let result = ingredients.value;

            if (selectedCategory.value && selectedCategory.value !== "Toutes") {
                result = result.filter(item => item.category === selectedCategory.value);
            }

            if (searchQuery.value.trim()) {
                const q = searchQuery.value.toLowerCase();
                result = result.filter(item =>
                    item.productName?.toLowerCase().includes(q) ||
                    item.category?.toLowerCase().includes(q) ||
                    item.productBrand?.toLowerCase().includes(q)
                );
            }

            if (seasonFilter.value === 'winter') {
                result = result.filter(item => Array.isArray(item.seasonMonths) && item.seasonMonths.some(m => WINTER.has(m)));
            } else if (seasonFilter.value === 'summer') {
                result = result.filter(item => Array.isArray(item.seasonMonths) && item.seasonMonths.some(m => SUMMER.has(m)));
            } else if (seasonFilter.value === 'frozen') {
                result = result.filter(item => item.isFrozen === true);
            }

            if (sortKey.value) {
                result = [...result].sort((a, b) => {
                    let va, vb;
                    if (sortKey.value === 'name') {
                        va = (a.productName || '').toLowerCase();
                        vb = (b.productName || '').toLowerCase();
                    } else if (sortKey.value === 'category') {
                        va = (a.category || '').toLowerCase();
                        vb = (b.category || '').toLowerCase();
                    } else if (sortKey.value === 'nutriscore') {
                        va = NUTRISCORE_ORDER[a.nutriscore] ?? 99;
                        vb = NUTRISCORE_ORDER[b.nutriscore] ?? 99;
                    } else if (sortKey.value === 'kcal') {
                        va = parseKcalNum(a);
                        vb = parseKcalNum(b);
                    }
                    if (va < vb) return sortDir.value === 'asc' ? -1 : 1;
                    if (va > vb) return sortDir.value === 'asc' ? 1 : -1;
                    return 0;
                });
            }

            return result;
        });

        // ---- Chargement ----
        onMounted(() => {
            watch(
                () => authStore.userInfos,
                async () => {
                    if (!authStore?.userid) return;
                    try {
                        isLoadingIngredients.value = true;
                        const [data, hiddenData, meta] = await Promise.all([
                            getBaseIngredients(),
                            getHiddenBaseIngredients(),
                            getCategoryMeta(),
                        ]);
                        const baseList = Object.entries(data).map(([id, value]) => ({
                            id, ...value, isHidden: false,
                        }));
                        const hiddenList = Object.entries(hiddenData).map(([id, value]) => ({
                            id, ...value, isHidden: true,
                        }));
                        ingredients.value = [...baseList, ...hiddenList];
                        categoryMeta.value = meta;
                    } catch (error) {
                        console.error("Erreur récupération ingrédients:", error);
                        errorMessage.value = "Erreur lors du chargement des ingrédients.";
                    } finally {
                        isLoadingIngredients.value = false;
                    }
                },
                { immediate: true }
            );
        });

        return {
            authStore,
            errorMessage,
            snack,
            router,
            searchQuery,
            selectedCategory,
            selectedIngredient,
            categories,
            addItemToUserCollection,
            addedIds,
            getKcal,
            selectIngredient,
            requestDelete,
            confirmDialogOpen,
            confirmDialogMessage,
            confirmDeleteBaseIngredient,
            cancelDeleteBaseIngredient,
            isLoadingIngredients,
            filteredIngredients,
            sortKey,
            sortDir,
            setSort,
            isAdmin,
            categoryCards,
            // dialog édition ingrédient
            editDialog,
            canSaveEdit,
            openEditDialog,
            saveEditIngredient,
            toggleEditStore,
            AVAILABLE_STORES,
            NUTRISCORE_OPTIONS,
            NOVA_OPTIONS,
            ingredientCategories,
            nutriscoreColor,
            novaColor,
            // dialog image catégorie
            categoryImageDialog,
            openCategoryImageDialog,
            saveCategoryImage,
            getSeasonIcon,
            seasonFilter,
            promoteToBase,
            isPromoting,
        };
    },
};
</script>

<style scoped>
    .searchRow { display: flex; align-items: center; }
    .searchField { width: 100%; }

    .categoryCards {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-bottom: 16px;
    }

    /* Card cover style */
    .catCard {
        position: relative;
        width: 110px;
        height: 110px;
        border-radius: 14px;
        overflow: hidden;
        cursor: pointer;
        font-family: inherit;
        transition: transform 0.15s, box-shadow 0.15s;
        border: 2px solid transparent;
        flex-shrink: 0;
    }
    .catCard:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(117,83,252,0.18);
    }
    .catCard--active {
        border-color: #7553FC;
        box-shadow: 0 0 0 3px rgba(117,83,252,0.22);
    }

    /* Image de fond */
    .catCard__bg {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        border-radius: 0;
    }

    /* Fallback sans image */
    .catCard__fallback {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #f0edfb, #e8e3f5);
    }
    .catCard__emoji { font-size: 36px; line-height: 1; }

    /* Gradient + texte en bas */
    .catCard__overlay {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 22px 8px 8px;
        background: linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 100%);
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
    }
    .catCard__label {
        font-size: 11px;
        font-weight: 700;
        color: #fff;
        text-shadow: 0 1px 3px rgba(0,0,0,0.4);
        line-height: 1.2;
    }
    .catCard__count {
        font-size: 10px;
        font-weight: 600;
        color: rgba(255,255,255,0.8);
        display: none;
    }
    .catCard--active .catCard__count { display: inline; }

    /* Crayon admin */
    .catCard__editBtn {
        position: absolute;
        top: 6px;
        right: 6px;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: rgba(255,255,255,0.9);
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #7553FC;
        box-shadow: 0 1px 4px rgba(0,0,0,0.2);
        transition: transform 0.15s, background 0.15s;
        z-index: 2;
    }
    .catCard__editBtn:hover { transform: scale(1.15); background: #fff; }

    /* Tri */
    .th-sortable {
        cursor: pointer;
        user-select: none;
        white-space: nowrap;
    }
    .th-sortable:hover { color: rgb(var(--v-theme-accent)); }
    .sort-arrow {
        font-size: 10px;
        margin-left: 4px;
        opacity: 0.45;
    }
    .th-sortable:hover .sort-arrow { opacity: 0.9; }

    /* Bouton ajouter — même style que BaseRecipeCard */
    .ing-add-btn {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 11px;
        font-weight: 500;
        color: rgb(var(--v-theme-accent));
        background: rgba(var(--v-theme-accent), 0.07);
        border: 0.5px solid rgba(var(--v-theme-accent), 0.25);
        border-radius: 999px;
        padding: 5px 12px;
        cursor: pointer;
        font-family: inherit;
        transition: background 0.15s;
        white-space: nowrap;
    }
    .ing-add-btn:hover:not(:disabled) {
        background: rgba(var(--v-theme-accent), 0.14);
    }
    .ing-add-btn--added {
        color: #2e7d32;
        background: rgba(46, 125, 50, 0.1);
        border-color: rgba(46, 125, 50, 0.35);
        cursor: not-allowed;
    }

    /* Tableau */
    .ingredient-row { cursor: pointer; transition: background 0.12s; min-height: 52px; height: 52px; }
    .ingredient-row:hover { background: rgba(0,0,0,0.04); }
    .row--selected { background: rgba(117,83,252,0.07) !important; }
    .ingredient-cell { vertical-align: middle !important; height: 52px; }
    .ingredient-content { display: flex; align-items: center; height: 100%; }

    /* Nutriments panel */
    .nutriment-line {
        display: flex;
        justify-content: space-between;
        padding: 3px 0;
        border-bottom: 1px solid rgba(0,0,0,0.05);
        font-size: 0.82rem;
    }

    /* Placeholder image */
    .ing-img-placeholder {
        width: 120px;
        height: 120px;
        background: rgba(0,0,0,0.04);
        border: 1px dashed rgba(0,0,0,0.18);
    }

    /* Dialog */
    .dialog-section-label {
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.6px;
        color: rgba(0,0,0,0.45);
        margin-bottom: 10px;
    }
    .store-grid { display: flex; flex-wrap: wrap; gap: 6px; }
    .store-chip { cursor: pointer; transition: all 0.15s ease; user-select: none; }
    .store-chip:hover { opacity: 0.82; }

    .rowCustom { display: flex; flex-direction: row; justify-content: flex-end; align-items: center; }

    .season-icon { margin-left: 5px; font-size: 14px; vertical-align: middle; }

    .season-filters { display: flex; gap: 8px; flex-wrap: wrap; }
    .season-filter-btn {
        font-family: inherit;
        font-size: 12px;
        font-weight: 500;
        padding: 5px 12px;
        border-radius: 999px;
        border: 1.5px solid rgba(0,0,0,0.12);
        background: transparent;
        color: rgba(0,0,0,0.55);
        cursor: pointer;
        transition: all 0.15s;
    }
    .season-filter-btn:hover { border-color: #7553FC; color: #7553FC; }
    .season-filter-btn--active { background: #7553FC; border-color: #7553FC; color: #fff; font-weight: 600; }
    .season-filter-btn--frozen.season-filter-btn--active { background: #e1f5fe; border-color: #4fc3f7; color: #0288d1; }
</style>
